import React, { useState } from 'react';

/* Validation */
import { Formik } from 'formik';
import validationSchema, { SkadeType } from './formConfig';

/* Gjensidige Core 20 Imports */
import utils from '@gjensidige/core-utilities-css';

/* Gjensidige Core 30 Imports */
import { Alert } from '@gjensidige/core-components/lib/alerts/default';
import { Container, Row, Col } from '@gjensidige/nci-core-grid';
import { Input } from '@gjensidige/core-components/lib/forms/input';
import { TextArea } from '@gjensidige/core-components/lib/forms/textarea';
import { InputGroupSimple } from '@gjensidige/core-components/lib/forms/input-group-simple';

/* API */
import axios from 'axios';
import { Endpoints } from '@internal/common';
import {Button} from "@gjensidige/core-components/lib/button";

const WritetoUsPrivate = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    customerNumber: '',
    subject: '',
    alreadyCustomer: '',
    skadetype: '',
    content: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState(false);

  const sendFormData = () => {
    setApiError(false);
    axios
      .post(Endpoints.WriteToUsPrivate, formData)
      .then(() => setSubmitted(true))
      .catch(() => setApiError(true));
  };

  const DEFAULT_SUCCESS_MSG =
    'Vi kontakter dig indenfor 1-3 arbejdsdage. Hvis det haster, er du velkommen til at ringe til os på telefonen 70 10 90 09';
  const SALES_SUCCESS_MSG =
    'Vi kontakter dig indenfor 1 arbejdsdag. Hvis det haster, er du velkommen til at ringe til os på telefonen 70 10 90 09';

  return (
    <Container className={utils['mt-2']}>
      {submitted ? (
        <Row className={utils['justify-content-center']}>
          <Alert
            alertType="success"
            showIcon
            text={
              formData.alreadyCustomer === 'Nej' &&
              formData.subject === 'Tilbud på en ny privatforsikring'
                ? SALES_SUCCESS_MSG
                : DEFAULT_SUCCESS_MSG
            }
            title="Tak for din henvendelse"
            className={utils['mb-3']}
          />
        </Row>
      ) : (
        <Formik
          enableReinitialize
          initialValues={{
            name: formData.name,
            email: formData.email,
            phoneNumber: formData.phoneNumber,
            customerNumber: formData.customerNumber,
            subject: formData.subject,
            alreadyCustomer: formData.alreadyCustomer,
            skadetype: formData.skadetype,
            content: formData.content
          }}
          validationSchema={validationSchema(formData)}
          onSubmit={() => {
            sendFormData();
          }}
        >
          {(props) => {
            const {
              handleSubmit,
              handleBlur,
              setFieldValue,
              touched,
              errors
            } = props;
            return (
              <form onSubmit={handleSubmit} className={utils['pb-3']}>
                <Row className={utils['justify-content-center']}>
                  <Col lg={6}>
                    <Input
                      id="name"
                      labelText="Navn *"
                      name="name"
                      className={utils['pb-2']}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setFieldValue('name', event.target.value);
                        setFormData({ ...formData, name: event.target.value });
                      }}
                      onBlur={handleBlur}
                      invalid={!!(touched.name && errors.name)}
                      errorText={errors.name}
                      value={formData.name}
                    />
                    <Input
                      id="email"
                      labelText="Din mail *"
                      name="email"
                      className={utils['pb-2']}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setFieldValue('email', event.target.value);
                        setFormData({
                          ...formData,
                          email: event.target.value
                        });
                      }}
                      onBlur={handleBlur}
                      invalid={!!(touched.email && errors.email)}
                      errorText={errors.email}
                      value={formData.email}
                    />
                    <Input
                      id="phone-number"
                      labelText="Telefonnummer"
                      name="phoneNumber"
                      className={utils['pb-2']}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setFieldValue('phoneNumber', event.target.value);
                        setFormData({
                          ...formData,
                          phoneNumber: event.target.value
                        });
                      }}
                      onBlur={handleBlur}
                      invalid={!!(touched.phoneNumber && errors.phoneNumber)}
                      errorText={errors.phoneNumber}
                      value={formData.phoneNumber}
                    />
                    <InputGroupSimple
                      vertical
                      header="Er du allerede kunde hos os? *"
                      id="alreadyCustomer"
                      className={utils['pb-2']}
                      name="alreadyCustomer"
                      centered={false}
                      onChange={(event) => {
                        setFormData({
                          ...formData,
                          alreadyCustomer: event.target.value,
                          skadetype: '',
                          subject: ''
                        });
                        setFieldValue('alreadyCustomer', event.target.value);
                      }}
                      options={[
                        {
                          label: 'Ja',
                          value: 'Ja'
                        },
                        {
                          label: 'Nej',
                          value: 'Nej'
                        }
                      ]}
                      type="radio"
                      value={formData.alreadyCustomer}
                      onBlur={handleBlur}
                      invalid={
                        !!(touched.alreadyCustomer && errors.alreadyCustomer)
                      }
                      errorText={errors.alreadyCustomer}
                    />
                    {formData.alreadyCustomer === 'Ja' ? (
                      <InputGroupSimple
                        vertical
                        header="Hvad drejer din henvendelse sig om? *"
                        id="subject-radio"
                        className={utils['pb-2']}
                        name="subject"
                        centered={false}
                        onChange={(event) => {
                          setFormData({
                            ...formData,
                            subject: event.target.value
                          });
                          setFieldValue('subject', event.target.value);
                        }}
                        options={[
                          {
                            label: 'Sundhedsforsikring person',
                            value: 'Sundhedsforsikring person'
                          },
                          {
                            label: 'Skade eller brug af sundhedsforsikringen',
                            value: 'Skade'
                          },
                          {
                            label: 'Opsigelse af forsikring',
                            value: 'Opsigelse af forsikring'
                          },
                          {
                            label: 'Mine private forsikringer',
                            value: 'Mine private forsikringer'
                          },
                          {
                            label: 'Erhvervsforsikringer',
                            value: 'Erhvervsforsikringer'
                          },
                          {
                            label: 'Landbrugsforsikringer',
                            value: 'Landbrugsforsikringer'
                          }
                        ]}
                        type="radio"
                        value={formData.subject}
                        onBlur={handleBlur}
                        invalid={!!(touched.subject && errors.subject)}
                        errorText={errors.subject}
                      />
                    ) : formData.alreadyCustomer === 'Nej' ? (
                      <InputGroupSimple
                        vertical
                        header="Hvad drejer din henvendelse sig om? *"
                        id="subject-radio"
                        className={utils['pb-2']}
                        name="subject"
                        centered={false}
                        onChange={(event) => {
                          setFormData({
                            ...formData,
                            subject: event.target.value
                          });
                          setFieldValue('subject', event.target.value);
                        }}
                        options={[
                          {
                            label: 'Tilbud på en ny privatforsikring',
                            value: 'Tilbud på en ny privatforsikring'
                          },
                          {
                            label: 'Henvendelse på vegne af anden kunde',
                            value: 'Henvendelse på vegne af anden kunde'
                          },
                          {
                            label: 'Spørgsmål til en skadessag',
                            value: 'Skade'
                          },
                          {
                            label: 'Andre henvendelser',
                            value: 'Andre henvendelser'
                          }
                        ]}
                        type="radio"
                        value={formData.subject}
                        onBlur={handleBlur}
                        invalid={!!(touched.subject && errors.subject)}
                        errorText={errors.subject}
                      />
                    ) : null}
                    {formData.subject === 'Skade' && (
                      <InputGroupSimple
                        vertical
                        header="Hvilken skade gælder det? *"
                        id="skadetype"
                        className={utils['pb-2']}
                        name="skadetype"
                        centered={false}
                        onChange={(event) => {
                          setFormData({
                            ...formData,
                            skadetype: event.target.value
                          });
                          setFieldValue('skadetype', event.target.value);
                        }}
                        options={Object.entries(SkadeType).map((key) => {
                          return { label: key[1], value: key[1] };
                        })}
                        type="radio"
                        value={formData.skadetype}
                        onBlur={handleBlur}
                        invalid={!!(touched.skadetype && errors.skadetype)}
                        errorText={errors.skadetype}
                      />
                    )}
                    {formData.alreadyCustomer === 'Ja' && (
                      <Input
                        id="customerNumber"
                        labelText={
                          formData.subject === 'Sundhedsforsikring'
                            ? 'Dit CPR- eller skadenummer *'
                            : 'Dit kunde-, police- eller skadenummer *'
                        }
                        name="customerNumber"
                        className={utils['pb-2']}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setFieldValue('customerNumber', event.target.value);
                          setFormData({
                            ...formData,
                            customerNumber: event.target.value
                          });
                        }}
                        onBlur={handleBlur}
                        invalid={
                          !!(touched.customerNumber && errors.customerNumber)
                        }
                        errorText={errors.customerNumber}
                        value={formData.customerNumber}
                      />
                    )}
                    <TextArea
                      id="content-textarea"
                      labelText="Hvad kan vi hjælpe dig med? *"
                      maxLength={500}
                      name="content"
                      className={utils['pb-2']}
                      value={formData.content}
                      onChange={(
                        event: React.ChangeEvent<HTMLTextAreaElement>
                      ) => {
                        setFieldValue('content', event.target.value);
                        setFormData({
                          ...formData,
                          content: event.target.value
                        });
                      }}
                      onBlur={handleBlur}
                      invalid={!!(touched.content && errors.content)}
                      errorText={errors.content}
                    />
                    <Button variant={'primary'} type="submit" flexible>
                      Send
                    </Button>
                  </Col>
                </Row>
              </form>
            );
          }}
        </Formik>
      )}
      {apiError && (
        <Row className={utils['justify-content-center']}>
          <Alert
            alertType="error"
            showIcon
            text="Der skete en fejl ved indsendelse. Prøv venligst igen senere"
            title="Der skete en fejl."
            className={utils['mb-3']}
          />
        </Row>
      )}
    </Container>
  );
};

export default WritetoUsPrivate;
